/*============================================================================
  Testimonials Section JavaScript
==============================================================================*/

class TestimonialsSection {
  constructor(container) {
    if (!container) return;

    this.container = container;

    // Prevent duplicate initialization
    if (this.container.testimonialsInstance) {
      return;
    }

    this.container.testimonialsInstance = this;

    this.timeout = null;
    this.sectionId = container.getAttribute('data-section-id');
    this.slideshow = container.querySelector(
      `#Testimonials-${this.sectionId}`
    );

    this.flickity = null;

    if (!this.slideshow) return;

    this.init();
  }

  init() {
    const slideCount = parseInt(
      this.slideshow.dataset.count || '0',
      10
    );

    if (slideCount === 0) return;

    this.defaults = {
      adaptiveHeight: true,
      avoidReflow: true,
      pageDots: true,
      prevNextButtons: false,
      cellAlign: 'center',
      contain: true,
      autoPlay: false,
      wrapAround: slideCount > 1
    };

    // Flickity already loaded
    if (typeof Flickity !== 'undefined') {
      this.initFlickity();
      return;
    }

    // Flickity not loaded
    this.loadFlickity().then(() => {
      if (typeof Flickity !== 'undefined') {
        this.initFlickity();
      } else {
        this.initFallbackSlider();
      }
    });
  }

  /*==========================================================================
    Initialize Flickity
  ==========================================================================*/

  initFlickity() {
    // Prevent duplicate Flickity instance
    if (this.flickity) return;

    this.flickity = new Flickity(
      this.slideshow,
      this.defaults
    );

    this.flickity.on('scroll', () => {
      this.updateCenterSlide();
    });

    this.flickity.on('settle', () => {
      this.updateCenterSlide();
    });

    this.updateCenterSlide();

    /*
      Small delay so Flickity has completely calculated
      dimensions, especially on product pages.
    */
    setTimeout(() => {
      if (this.flickity) {
        this.flickity.resize();
        this.updateCenterSlide();
      }
    }, 100);

    /*
      Move slightly to the next slide so user can see
      that the section is a carousel.
    */
    if (parseInt(this.slideshow.dataset.count || '0', 10) > 2) {
      this.timeout = setTimeout(() => {
        if (this.flickity) {
          this.flickity.next();
        }
      }, 1000);
    }
  }

  /*==========================================================================
    Load Flickity if it is not already available
  ==========================================================================*/

  loadFlickity() {
    return new Promise((resolve) => {

      // Already loading
      if (window.testimonialsFlickityLoading) {
        window.testimonialsFlickityLoading.then(resolve);
        return;
      }

      window.testimonialsFlickityLoading = new Promise((done) => {

        // Load Flickity CSS
        if (!document.querySelector(
          'link[href*="flickity"]'
        )) {
          const css = document.createElement('link');

          css.rel = 'stylesheet';
          css.href =
            'https://cdn.jsdelivr.net/npm/flickity@2/dist/flickity.min.css';

          document.head.appendChild(css);
        }

        // Load Flickity JS
        if (typeof Flickity !== 'undefined') {
          done();
          return;
        }

        const script = document.createElement('script');

        script.src =
          'https://cdn.jsdelivr.net/npm/flickity@2/dist/flickity.pkgd.min.js';

        script.onload = () => {
          done();
        };

        script.onerror = () => {
          done();
        };

        document.head.appendChild(script);
      });

      window.testimonialsFlickityLoading.then(resolve);
    });
  }

  /*==========================================================================
    Center Slide
  ==========================================================================*/

  updateCenterSlide() {
    if (!this.flickity) return;

    const slides = this.slideshow.querySelectorAll(
      '.testimonials-slide'
    );

    if (!slides.length) return;

    const containerRect =
      this.slideshow.getBoundingClientRect();

    const containerCenter =
      containerRect.left +
      containerRect.width / 2;

    let closestSlide = null;
    let closestDistance = Infinity;

    slides.forEach((slide) => {

      const rect =
        slide.getBoundingClientRect();

      const center =
        rect.left + rect.width / 2;

      const distance =
        Math.abs(center - containerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    slides.forEach((slide) => {
      slide.classList.toggle(
        'is-selected',
        slide === closestSlide
      );
    });
  }

  /*==========================================================================
    Fallback if Flickity cannot load
  ==========================================================================*/

  initFallbackSlider() {
    const slides =
      this.slideshow.querySelectorAll(
        '.testimonials-slide'
      );

    if (slides.length <= 1) {
      if (slides[0]) {
        slides[0].style.display = 'block';
        slides[0].style.opacity = '1';
      }

      return;
    }

    let currentSlide = 0;

    slides.forEach((slide, index) => {
      slide.style.display =
        index === 0 ? 'block' : 'none';

      slide.style.opacity =
        index === 0 ? '1' : '0';
    });

    this.fallbackInterval = setInterval(() => {

      slides[currentSlide].style.opacity = '0';

      setTimeout(() => {

        slides[currentSlide].style.display = 'none';

        currentSlide =
          (currentSlide + 1) % slides.length;

        slides[currentSlide].style.display = 'block';

        requestAnimationFrame(() => {
          slides[currentSlide].style.opacity = '1';
        });

      }, 300);

    }, 4000);
  }

  /*==========================================================================
    Shopify Theme Editor
  ==========================================================================*/

  onUnload() {

    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
    }

    this.container.testimonialsInstance = null;
  }

  onBlockSelect(evt) {

    if (!this.slideshow) return;

    const slide =
      this.slideshow.querySelector(
        `.testimonials-slide--${evt.detail.blockId}`
      );

    if (!slide) return;

    const index =
      parseInt(slide.dataset.index || '0', 10);

    if (this.flickity) {

      this.flickity.select(index);

      if (
        typeof this.flickity.pausePlayer === 'function'
      ) {
        this.flickity.pausePlayer();
      }
    }
  }

  onBlockDeselect() {

    if (
      this.flickity &&
      typeof this.flickity.unpausePlayer === 'function'
    ) {
      this.flickity.unpausePlayer();
    }
  }
}


/*============================================================================
  Initialize Testimonials
==============================================================================*/

function initTestimonialsSections(scope = document) {

  const sections =
    scope.querySelectorAll(
      '[data-section-type="testimonials"]'
    );

  sections.forEach((section) => {

    if (!section.testimonialsInstance) {
      new TestimonialsSection(section);
    }

  });
}


/*============================================================================
  Initial Page Load
==============================================================================*/

function initializeTestimonials() {

  initTestimonialsSections(document);

}


/*
  Handle both cases:

  1. JS loaded before DOMContentLoaded
  2. JS loaded after DOMContentLoaded
*/

if (document.readyState === 'loading') {

  document.addEventListener(
    'DOMContentLoaded',
    initializeTestimonials
  );

} else {

  initializeTestimonials();

}


/*============================================================================
  Shopify Theme Editor Events
==============================================================================*/

document.addEventListener(
  'shopify:section:load',
  function(event) {

    const section =
      event.target.querySelector(
        '[data-section-type="testimonials"]'
      );

    if (section) {
      new TestimonialsSection(section);
    }

  }
);


document.addEventListener(
  'shopify:section:unload',
  function(event) {

    const section =
      event.target.querySelector(
        '[data-section-type="testimonials"]'
      );

    if (
      section &&
      section.testimonialsInstance
    ) {
      section.testimonialsInstance.onUnload();
    }

  }
);


document.addEventListener(
  'shopify:block:select',
  function(event) {

    const section =
      event.target.closest(
        '[data-section-type="testimonials"]'
      );

    if (
      section &&
      section.testimonialsInstance
    ) {
      section.testimonialsInstance.onBlockSelect(event);
    }

  }
);


document.addEventListener(
  'shopify:block:deselect',
  function(event) {

    const section =
      event.target.closest(
        '[data-section-type="testimonials"]'
      );

    if (
      section &&
      section.testimonialsInstance
    ) {
      section.testimonialsInstance.onBlockDeselect();
    }

  }
);